import * as React from 'react';
import styled, { css } from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { ProductCode } from 'common/components/cookbook/ProductCode';
import { Stack } from 'common/components/cookbook/Stack';
import { Thumbnail } from 'common/components/cookbook/Thumbnail';
import { Typography as T } from 'common/components/cookbook/Typography/Typography';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

const Root = styled(ContentContainer)<{ $isSelectable: boolean }>`
  display: flex;
  border-bottom: 1px solid ${color.border};

  ${({ $isSelectable }) =>
    $isSelectable &&
    css`
      cursor: pointer;

      &:hover {
        background: ${color.bgSurfaceTertiaryHovered};
      }

      &:active {
        background: ${color.bgSurfaceTertiaryPressed};
      }
    `}
`;

const LeftColumn = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const RightColumnRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  & + & {
    margin-top: 8px;
  }
`;

const Name = styled(T.body)`
  font-weight: 500;
`;

const ImageContainer = styled.div`
  position: relative;
`;

const SelectedIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -4px;
  right: -4px;
  height: 24px;
  width: 24px;
  background: ${color.bg};
  border-radius: 50%;
`;

export const Item: React.FC<{
  name: string;
  gtin?: string;
  image?: string;
  productCode?: string;
  onClick?: () => void;
  bottomContent?: React.ReactNode;
  isSelected?: boolean;
  dataTestId?: string;
  styles?: React.CSSProperties;
}> = ({
  name,
  gtin,
  image,
  productCode,
  onClick,
  bottomContent,
  dataTestId,
  styles = {},
  isSelected,
}) => {
  const formatMessage = useFormatMessage();

  return (
    <Root
      verticalPadding="sm"
      data-test-id={dataTestId ? `warehouse-item-${dataTestId}` : 'warehouse-item'}
      onClick={onClick}
      $isSelectable={onClick !== undefined}
      style={styles}
    >
      <LeftColumn>
        <ImageContainer>
          <Thumbnail imgSrc={image} altText={name} />
          {isSelected && (
            <SelectedIndicator>
              <Icon source={icons.checkmarkCircleFilled} size="small" fill={color.textBrand} />
            </SelectedIndicator>
          )}
        </ImageContainer>
      </LeftColumn>
      <RightColumn>
        <RightColumnRow>
          <Name data-test-id="warehouse-item-name">{name}</Name>
        </RightColumnRow>
        <RightColumnRow>
          <Stack direction="row" wrap="wrap" gap="regularMedium">
            <Stack direction="row" alignItems="center" gap="regularMedium">
              <Icon source={icons.barcode} size="small" fill={color.text} />
              <T.body data-test-id="warehouse-item-gtin">
                {gtin || formatMessage('common.not_available')}
              </T.body>
            </Stack>
            {productCode !== undefined && <ProductCode code={productCode} />}
          </Stack>
        </RightColumnRow>
        {bottomContent && <RightColumnRow>{bottomContent}</RightColumnRow>}
      </RightColumn>
    </Root>
  );
};
